import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const Chat = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const presetUser = params.get('to');

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [loadingConv, setLoadingConv] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [isHoveringItemHeader, setIsHoveringItemHeader] = useState(false);
  const [hoveredConvId, setHoveredConvId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  useEffect(() => {
    // Reset hover state when selected conversation changes
    setIsHoveringItemHeader(false);
    setHoveredConvId(null);
  }, [selected]);

  useEffect(() => {
    const to = presetUser;
    if (to && user) {
      startConversation(to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetUser, user]);

  const loadConversations = async () => {
    try {
      setLoadingConv(true);
      const res = await axios.get('/api/chat/conversations');
      const convs = Array.isArray(res.data) ? res.data : [];
      setConversations(convs);
      // Auto-select first if none selected
      if (convs.length > 0 && !selected) {
        setSelected(convs[0].id);
        loadMessages(convs[0].id);
      }
    } catch (e) {
      console.error('Lỗi tải hội thoại:', e);
      setConversations([]);
    } finally {
      setLoadingConv(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      setLoadingMsg(true);
      const res = await axios.get(`/api/chat/messages/${conversationId}`);
      const msgs = Array.isArray(res.data) ? res.data : [];
      setMessages(msgs);
    } catch (e) {
      console.error('Lỗi tải tin nhắn:', e);
      setMessages([]);
    } finally {
      setLoadingMsg(false);
    }
  };

  const startConversation = async (otherUserId) => {
    try {
      const res = await axios.post('/api/chat/start', { other_user_id: Number(otherUserId) });
      const conv = res.data;
      await loadConversations();
      setSelected(conv.id);
      loadMessages(conv.id);
    } catch (e) {
      console.error('Lỗi tạo hội thoại:', e);
      alert('Không thể tạo hội thoại.');
    }
  };

  const handleSelect = (convId) => {
    setSelected(convId);
    loadMessages(convId);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!content.trim() || !selected) return;
    try {
      await axios.post(`/api/chat/messages/${selected}`, { content });
      // Reload messages to get sender info
      await loadMessages(selected);
      setContent('');
    } catch (e) {
      console.error('Lỗi gửi tin nhắn:', e);
      alert('Không thể gửi tin nhắn');
    }
  };

  const selectedConv = useMemo(
    () => conversations.find((c) => c.id === selected),
    [conversations, selected]
  );

  if (!user) return null;

  return (
    <div className="container" style={{ marginTop: '32px' }}>
      <div className="card chat-container" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '16px', minHeight: '600px' }}>
        <div style={{ borderRight: '1px solid #eee', paddingRight: '12px', display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <h3 style={{ margin: 0 }}>Hội thoại</h3>
            <button className="btn btn-outline" style={{ padding: '8px 12px' }} onClick={loadConversations} disabled={loadingConv}>
              Làm mới
            </button>
          </div>
          {loadingConv ? (
            <div className="loading">Đang tải...</div>
          ) : conversations.length === 0 ? (
            <div className="empty-state" style={{ padding: '12px 0' }}>Chưa có hội thoại</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flex: 1 }}>
              {conversations.map((c) => {
                const isOwner = c.owner_id === user.id;
                const partner = isOwner ? c.requester : c.owner;
                const owner = c.owner;
                const partnerName = partner?.full_name || partner?.username || `User #${isOwner ? c.requester_id : c.owner_id}`;
                const ownerName = owner?.full_name || owner?.username || `User #${c.owner_id}`;
                const itemTitle = c.item?.title || 'Món đồ';
                const itemImage = c.item?.image_url;
                const itemDescription = c.item?.description || '';
                const isHovered = hoveredConvId === c.id;
                const isSelected = selected === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(c.id)}
                    className="btn btn-outline"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      padding: '10px 12px',
                      background: isSelected ? '#7b1fa2' : (isHovered ? '#e8e8e8' : '#f5f5f5'),
                      borderColor: isSelected ? '#7b1fa2' : (isHovered ? '#bbb' : '#ddd'),
                      color: isSelected ? '#fff' : '#333',
                      textAlign: 'left',
                      width: '100%',
                      gap: '10px',
                      fontWeight: isSelected ? '600' : '400',
                      transition: 'all 0.2s',
                      boxShadow: isSelected ? '0 2px 4px rgba(123, 31, 162, 0.2)' : 'none'
                    }}
                    onMouseEnter={() => setHoveredConvId(c.id)}
                    onMouseLeave={() => setHoveredConvId(null)}
                  >
                    {itemImage ? (
                      <img 
                        src={itemImage} 
                        alt={itemTitle}
                        style={{ 
                          width: '50px', 
                          height: '50px', 
                          objectFit: 'cover', 
                          borderRadius: '8px',
                          flexShrink: 0
                        }} 
                      />
                    ) : (
                      <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        background: '#e0e0e0', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        flexShrink: 0
                      }}>
                        📦
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: '14px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap', 
                            color: isSelected ? '#fff' : (isHovered ? '#222' : '#333'),
                            transition: 'color 0.2s'
                          }}>
                            {itemTitle}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: isSelected ? '#f0f0f0' : (isHovered ? '#555' : '#666'), 
                            marginTop: '2px',
                            transition: 'color 0.2s'
                          }}>
                            {isOwner ? `Người xin: ${partnerName}` : `Người cho: ${ownerName}`}
                          </div>
                        </div>
                        {c.last_message && (
                          <span style={{ 
                            fontSize: '11px', 
                            color: isSelected ? '#e0e0e0' : (isHovered ? '#666' : '#777'), 
                            flexShrink: 0,
                            transition: 'color 0.2s'
                          }}>
                            {new Date(c.last_message.created_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      {itemDescription && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: isSelected ? '#e0e0e0' : (isHovered ? '#888' : '#999'), 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          transition: 'color 0.2s'
                        }}>
                          {itemDescription}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'hidden' }}>
          {!selectedConv ? (
            <div className="empty-state" style={{ padding: '20px' }}>
              Chọn một hội thoại để xem tin nhắn
            </div>
          ) : (
            <>
              {/* Item info header */}
              <div 
                onClick={() => selectedConv.item?.id && navigate(`/item/${selectedConv.item.id}`)}
                style={{ 
                  padding: '12px 16px', 
                  background: isHoveringItemHeader ? '#e8e8e8' : '#f5f5f5', 
                  borderRadius: '8px',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  flexShrink: 0,
                  cursor: selectedConv.item?.id ? 'pointer' : 'default',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={() => setIsHoveringItemHeader(true)}
                onMouseLeave={() => setIsHoveringItemHeader(false)}
              >
                {selectedConv.item?.image_url ? (
                  <img 
                    src={selectedConv.item.image_url} 
                    alt={selectedConv.item.title}
                    style={{ 
                      width: '60px', 
                      height: '60px', 
                      objectFit: 'cover', 
                      borderRadius: '8px',
                      flexShrink: 0
                    }} 
                  />
                ) : (
                  <div style={{ 
                    width: '60px', 
                    height: '60px', 
                    background: '#e0e0e0', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    flexShrink: 0
                  }}>
                    📦
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    color: isHoveringItemHeader ? '#5a0d7a' : (selectedConv.item?.id ? '#7b1fa2' : '#333'),
                    transition: 'color 0.2s'
                  }}>
                    {selectedConv.item?.title || 'Món đồ'}
                  </h3>
                  {selectedConv.item?.description && (
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '12px', 
                      color: isHoveringItemHeader ? '#555' : '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.2s'
                    }}>
                      {selectedConv.item.description}
                    </p>
                  )}
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '12px', 
                    color: isHoveringItemHeader ? '#777' : '#999',
                    transition: 'color 0.2s'
                  }}>
                    {selectedConv.owner_id === user.id 
                      ? `Người xin: ${selectedConv.requester?.full_name || selectedConv.requester?.username || `User #${selectedConv.requester_id}`}`
                      : `Người cho: ${selectedConv.owner?.full_name || selectedConv.owner?.username || `User #${selectedConv.owner_id}`}`
                    }
                  </p>
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  minHeight: '320px',
                  maxHeight: '420px',
                  overflowY: 'auto',
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #eee'
                }}
              >
                {loadingMsg ? (
                  <div className="loading">Đang tải...</div>
                ) : messages.length === 0 ? (
                  <div className="empty-state" style={{ padding: '12px 0' }}>Chưa có tin nhắn</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {messages.map((m) => {
                      const isMe = m.sender_id === user.id;
                      const senderName = m.sender?.full_name || m.sender?.username || `User #${m.sender_id}`;
                      return (
                        <div
                          key={m.id}
                          style={{
                            alignSelf: isMe ? 'flex-end' : 'flex-start',
                            background: isMe ? '#e1bee7' : '#fff',
                            color: '#4a148c',
                            padding: '10px 12px',
                            borderRadius: '12px',
                            maxWidth: '70%',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            border: isMe ? 'none' : '1px solid #ddd'
                          }}
                        >
                          {!isMe && (
                            <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#7b1fa2' }}>
                              {senderName}
                            </div>
                          )}
                          <div style={{ fontSize: '14px', lineHeight: 1.5 }}>{m.content}</div>
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                            {new Date(m.created_at).toLocaleString('vi-VN')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <form onSubmit={handleSend} style={{ display: 'flex', gap: '10px' }}>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Nhập tin nhắn..."
                  rows="3"
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
                />
                <button type="submit" className="btn btn-primary" style={{ alignSelf: 'flex-end', padding: '12px 16px' }}>
                  Gửi
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;


